import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaUserTie, FaEnvelope, FaPhone, FaChartLine, FaUsers, 
  FaClock, FaCheckCircle, FaArrowLeft, FaUserFriends, FaBriefcase, 
  FaPhoneAlt, FaCalendarAlt, FaStar, FaTrophy, FaUserCircle } from "react-icons/fa";
import { getTmReportById } from "../../services/reportService";
import "./TeamReport.css";

const TmReportDetails = () => {
  const { tmId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDetails();
  }, [tmId]);

  const loadDetails = async () => {
    setLoading(true);
    const res = await getTmReportById(tmId);
    setData(res);
    setLoading(false);
  };

  const getPerformanceLevel = (closed, total) => {
    const percentage = total > 0 ? (closed / total) * 100 : 0;
    if (percentage >= 70) return { level: "Excellent", icon: <FaTrophy />, color: "#10b981" };
    if (percentage >= 50) return { level: "Good", icon: <FaStar />, color: "#f59e0b" };
    if (percentage >= 30) return { level: "Average", icon: <FaChartLine />, color: "#6366f1" };
    return { level: "Needs Improvement", icon: <FaClock />, color: "#ef4444" };
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading team manager details...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="error-container">
        <FaUserCircle className="error-icon" />
        <h3>No Data Found</h3>
        <p>Unable to load team manager details</p>
        <button onClick={() => navigate(-1)} className="back-btn">
          <FaArrowLeft /> Go Back
        </button>
      </div>
    );
  }

  const performance = getPerformanceLevel(data.summary?.closed_leads || 0, data.summary?.total_leads || 0);

  return (
    <div className="tm-details-container">
      {/* Header with Back Button */}
      <div className="details-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <FaArrowLeft /> Back
        </button>
        <div className="header-badge">
          <FaUserTie />
          <span>Team Manager Report</span>
        </div>
      </div>

      {/* TM Profile Card */}
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">
            <FaUserTie />
          </div>
          <div className="profile-info">
            <h3>{data.tm?.name}</h3>
            <p className="profile-role">Team Manager</p>
            <div className="performance-badge" style={{ background: performance.color + '20', color: performance.color }}>
              {performance.icon}
              <span>{performance.level}</span>
            </div>
          </div>
        </div>
        <div className="profile-details">
          <div className="detail-item">
            <FaEnvelope />
            <div>
              <label>Email Address</label>
              <span>{data.tm?.email}</span>
            </div>
          </div>
          <div className="detail-item">
            <FaPhone />
            <div>
              <label>Mobile Number</label>
              <span>{data.tm?.mobile}</span>
            </div>
          </div>
          <div className="detail-item">
            <FaUserFriends />
            <div>
              <label>Team Size</label>
              <span>{data.sales?.length || 0} Sales Executives</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards-grid">
        <div className="summary-card-detail">
          <div className="card-icon total-icon">
            <FaChartLine />
          </div>
          <div className="card-content">
            <span className="card-label">Total Leads</span>
            <span className="card-value">{data.summary?.total_leads || 0}</span>
          </div>
        </div>
        <div className="summary-card-detail">
          <div className="card-icon assigned-icon">
            <FaUsers />
          </div>
          <div className="card-content">
            <span className="card-label">Assigned Leads</span>
            <span className="card-value">{data.summary?.assigned_leads || 0}</span>
          </div>
        </div>
        <div className="summary-card-detail">
          <div className="card-icon pending-icon">
            <FaClock />
          </div>
          <div className="card-content">
            <span className="card-label">Pending Followups</span>
            <span className="card-value">{data.summary?.pending_followups || 0}</span>
          </div>
        </div>
        <div className="summary-card-detail">
          <div className="card-icon closed-icon">
            <FaCheckCircle />
          </div>
          <div className="card-content">
            <span className="card-label">Closed Leads</span>
            <span className="card-value">{data.summary?.closed_leads || 0}</span>
          </div>
        </div>
      </div>

      {/* Sales Team Section */}
      <div className="sales-section">
        <div className="section-header">
          <FaBriefcase className="section-icon" />
          <h3>Sales Team Members</h3>
          <span className="team-count">{data.sales?.length || 0} Members</span>
        </div>

        <div className="table-wrapper">
          <table className="sales-table">
            <thead>
              <tr>
                <th>Sales Executive</th>
                <th>Contact</th>
                <th>Total Leads</th>
                <th>Pending</th>
                <th>Closed</th>
                <th>Performance</th>
              </tr>
            </thead>
            <tbody>
              {data.sales?.length === 0 ? (
                <tr className="empty-row">
                  <td colSpan="6">
                    <div className="empty-content">
                      <FaUserFriends className="empty-icon" />
                      <p>No sales team members found</p>
                      <small>This team manager hasn't been assigned any sales executives yet</small>
                    </div>
                  </td>
                </tr>
              ) : (
                data.sales.map((s, index) => {
                  const salesPerformance = getPerformanceLevel(s.closed_leads || 0, s.total_leads || 0);
                  return (
                    <tr key={s.sales_id || index}>
                      <td>
                        <div className="sales-info">
                          <div className="sales-avatar">
                            <FaUserCircle />
                          </div>
                          <div className="sales-details">
                            <strong>{s.sales_name}</strong>
                            <small>ID: {s.sales_id}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="contact-details">
                          <span><FaEnvelope /> {s.email}</span>
                          <span><FaPhoneAlt /> {s.mobile}</span>
                        </div>
                      </td>
                      <td className="stat-cell">
                        <span className="stat-number">{s.total_leads || 0}</span>
                      </td>
                      <td className="stat-cell">
                        <span className="stat-number pending">{s.pending_followups || 0}</span>
                      </td>
                      <td className="stat-cell">
                        <span className="stat-number closed">{s.closed_leads || 0}</span>
                      </td>
                      <td>
                        <div className="performance-indicator" style={{ 
                          background: salesPerformance.color + '20', 
                          color: salesPerformance.color 
                        }}>
                          {salesPerformance.icon}
                          <span>{salesPerformance.level}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TmReportDetails;